import React from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { getApiErrorCode, getApiErrorMessage } from '@/client/utils/errors';

export interface ActionFlags {
  force?: boolean;
  bypass_quota?: boolean;
}

/**
 * Write-action dialog: required reason, optional extra fields (children), and the
 * two-step confirms the virtual-tours API asks for:
 *  - 409 `listing_live`  -> show the backend message, button becomes `forceLabel`, resend with force.
 *  - 409 `quota_blocked` -> show the message; with admin.virtual_tour.quota_bypass offer
 *    "Link anyway (bypass entitlement)" which resends with bypass_quota.
 * Any other failure shows the backend `error_description` inline.
 */
export const ActionDialog: React.FC<{
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  successMessage: string;
  /** Label for the second-step confirm on 409 listing_live (e.g. "Archive anyway"). */
  forceLabel?: string;
  /** Enables the quota_blocked bypass offer (link / move). */
  allowBypass?: boolean;
  /** Label for the bypass second step (default link wording). */
  bypassLabel?: string;
  /** False while required extra fields are incomplete. */
  valid?: boolean;
  submit: (reason: string, flags: ActionFlags) => Promise<unknown>;
  onClose: () => void;
  children?: React.ReactNode;
}> = ({
  open,
  title,
  description,
  confirmLabel,
  successMessage,
  forceLabel,
  allowBypass,
  bypassLabel = 'Link anyway (bypass entitlement)',
  valid = true,
  submit,
  onClose,
  children,
}) => {
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [step, setStep] = React.useState<'force' | 'bypass' | null>(null);
  const canBypass = useGate(PERMISSIONS.virtualTour.quotaBypass).allowed;

  React.useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
      setStep(null);
    }
  }, [open]);

  const run = async (flags: ActionFlags) => {
    setBusy(true);
    try {
      await submit(reason.trim(), flags);
      toast.success(successMessage);
      onClose();
    } catch (err) {
      const code = getApiErrorCode(err);
      setError(getApiErrorMessage(err, 'The action failed.'));
      setStep(
        code === 'listing_live' && forceLabel ? 'force' : code === 'quota_blocked' && allowBypass && canBypass ? 'bypass' : null
      );
    } finally {
      setBusy(false);
    }
  };

  const confirm = () => {
    setError(null);
    return run(step === 'force' ? { force: true } : step === 'bypass' ? { bypass_quota: true } : {});
  };

  const label = step === 'force' ? forceLabel : step === 'bypass' ? bypassLabel : confirmLabel;

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Reason</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Why you are doing this, for the audit trail"
            className="w-full rounded-md border px-3 py-2 text-sm"
          />
        </label>
        {error && (
          <div
            role="alert"
            className={`rounded-md border px-3 py-2 text-sm ${
              step ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-red-100 bg-red-50 text-red-600'
            }`}
          >
            {error}
          </div>
        )}
        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={step ? 'destructive' : 'default'}
            disabled={busy || !reason.trim() || !valid}
            onClick={confirm}
          >
            {busy ? 'Working…' : label}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ActionDialog;
